import { Layer, Popup, Source, useMap } from "@vis.gl/react-maplibre";
import type { MapGeoJSONFeature } from "maplibre-gl";
import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import { getAliveMap, useMapLibreLayerPointer } from "@ryde/controls";
import { ITrackingRenderState, TLngLatTuple, useFormats } from "@ryde/shared";
import { rydeColors } from "@ryde/theme";

import { TrackingPointTooltipContent } from "../../tracking-trail/tracking-point-tooltip-content";
import {
  DEGREES_PER_RADIAN,
  TRAIL_POINT_RADIUS_PX,
  TRAIL_TOOLTIP_OFFSET_PX,
} from "../route-block-maplibre.constants";
import { DETOUR_DOT_STYLE } from "./maplibre-tracking-trail-points.constants";
import {
  IHoveredTrailPoint,
  ITrailDotStyle,
} from "./maplibre-tracking-trail-points.models";
import { buildTrailPointsFeatureCollection } from "./maplibre-tracking-trail-points.utils";
import { resolveTrailBearings } from "./maplibre-trail-bearing.utils";
import {
  ensureTrailDotImage,
  trailDotImageId,
} from "./maplibre-trail-dot-image.utils";

interface IMapLibreTrackingTrailPointsProps {
  id: string;
  renderState: ITrackingRenderState;
  markerIndices: readonly number[];
  color: string;
  /**
   * Perpendicular shift (screen px) the dots must share with their trail line, so they ride the drawn line
   * instead of the raw GPS track. Applied by MapLibre via `icon-offset` — which is why these are symbols
   * rather than circles: `circle-translate` is a single constant for the layer, not a per-point direction.
   */
  offsetPx?: number;
}

/**
 * Decimated GPS trail dots for live-map advanced mode with a hover tooltip (time / speed / GPS-gap note)
 * driven by MapLibre's layer-scoped pointer events — no per-point hit-testing. Leaflet twin:
 * `TrackingTrailPointsLayer`.
 */
export const MapLibreTrackingTrailPoints: FC<
  IMapLibreTrackingTrailPointsProps
> = ({ id, renderState, markerIndices, color, offsetPx }) => {
  const { t } = useTranslation();
  const { formatTimeNormalized } = useFormats();
  const { current: mapRef } = useMap();
  const [hoveredPoint, setHoveredPoint] = useState<IHoveredTrailPoint | null>(
    null,
  );

  const dotsLayerId = `${id}-dots`;

  const plainDotStyle = useMemo<ITrailDotStyle>(
    () => ({
      color,
      strokeColor: rydeColors.white,
      radiusPx: TRAIL_POINT_RADIUS_PX,
      strokeWidthPx: 0,
    }),
    [color],
  );

  useEffect(() => {
    const map = getAliveMap(mapRef);
    if (!map) {
      return;
    }

    const registerImages = (): void => {
      if (!map.isStyleLoaded()) {
        return;
      }
      ensureTrailDotImage(map, plainDotStyle);
      ensureTrailDotImage(map, DETOUR_DOT_STYLE);
    };

    registerImages();
    map.on("style.load", registerImages);
    return () => {
      getAliveMap(mapRef)?.off("style.load", registerImages);
    };
  }, [mapRef, plainDotStyle]);

  // Bearings depend only on the trail, so they survive zoom changes and decimation churn — computing them
  // per drawn dot meant rescanning the same points on every zoom, for up to 70k dots across the map.
  const bearings = useMemo(
    () => resolveTrailBearings(renderState),
    [renderState],
  );

  const pointsFeatureCollection = useMemo(
    () =>
      buildTrailPointsFeatureCollection({
        state: renderState,
        markerIndices,
        bearings,
        formatTime: formatTimeNormalized,
        formatSpeed: speed => t("rideMap.TRACKING_SPEED", { speed }),
        gapNote: t("rideMap.TRACKING_POINT_GAP"),
      }),
    [markerIndices, renderState, bearings, t, formatTimeNormalized],
  );

  const handleHover = useEvent((feature: MapGeoJSONFeature): void => {
    if (feature.geometry.type !== "Point") {
      return;
    }

    const [longitude, latitude] = feature.geometry.coordinates as TLngLatTuple;

    setHoveredPoint({
      longitude,
      latitude,
      bearing: Number(feature.properties?.bearing ?? 0),
      time: String(feature.properties?.time ?? ""),
      speed: String(feature.properties?.speed ?? ""),
      gapNote: String(feature.properties?.gapNote ?? ""),
    });
  });
  const handleLeave = useEvent(() => setHoveredPoint(null));

  useMapLibreLayerPointer({
    layerId: dotsLayerId,
    enabled: markerIndices.length > 0,
    onHover: handleHover,
    onLeave: handleLeave,
  });

  if (!markerIndices.length) {
    return null;
  }

  return (
    <>
      <Source id={id} type="geojson" data={pointsFeatureCollection}>
        <Layer
          id={dotsLayerId}
          type="symbol"
          layout={{
            ["icon-image"]: [
              "case",
              ["==", ["get", "isDetour"], 1],
              trailDotImageId(DETOUR_DOT_STYLE),
              trailDotImageId(plainDotStyle),
            ],
            ["icon-rotate"]: ["get", "bearing"],
            ["icon-rotation-alignment"]: "map",
            ["icon-pitch-alignment"]: "map",
            ["icon-allow-overlap"]: true,
            ["icon-ignore-placement"]: true,
            // The icon is rotated to the travel bearing (0 = north), so its local +x axis points to the
            // right of travel — the same side `line-offset` shifts the trail line to.
            ...(offsetPx ? { ["icon-offset"]: [offsetPx, 0] } : {}),
          }}
        />
      </Source>

      {hoveredPoint && (
        <Popup
          longitude={hoveredPoint.longitude}
          latitude={hoveredPoint.latitude}
          anchor="bottom"
          /*
           * The feature sits at the true GPS position while `icon-offset` draws the dot to the right of
           * travel, so the popup has to make the same trip or it points at empty map. Screen axes: a bearing
           * b runs (sin b, -cos b), so its right-hand normal is (cos b, sin b).
           */
          offset={[
            (offsetPx ?? 0) *
              Math.cos(hoveredPoint.bearing / DEGREES_PER_RADIAN),
            (offsetPx ?? 0) *
              Math.sin(hoveredPoint.bearing / DEGREES_PER_RADIAN) -
              TRAIL_TOOLTIP_OFFSET_PX,
          ]}
          closeButton={false}
          closeOnClick={false}
          className="custom-tracking-tooltip"
        >
          <TrackingPointTooltipContent
            time={hoveredPoint.time}
            speed={hoveredPoint.speed}
            gapNote={hoveredPoint.gapNote || undefined}
          />
        </Popup>
      )}
    </>
  );
};
